import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Ban, CheckCircle2, ChevronLeft, ChevronRight, Download, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import * as XLSX from 'xlsx';
import { authFetch } from '../../utils/authFetch';
import { zh } from '../../utils/zh';
import { fieldText, previewRecipients, recipientExportRows, type RecipientFilter, type RecipientPage, type ReviewedRecipient } from './recipientReview';

const PAGE_SIZE = 25;
const FILTERS: Array<{ key: RecipientFilter; label: string; count: 'total' | 'hardBounced' | 'failed' | 'excluded' }> = [
    { key: 'hard_bounced', label: '硬退信', count: 'hardBounced' },
    { key: 'all', label: "全部联系人", count: 'total' },
    { key: 'failed', label: "发送失败／待重试", count: 'failed' },
    { key: 'excluded', label: "已排除", count: 'excluded' },
];

async function loadPage(params: URLSearchParams, signal?: AbortSignal): Promise<RecipientPage> {
    const response = await authFetch(`/bulk-send/recipients?${params}`, { signal });
    if (response.status === 404) throw new Error("服务更新后即可使用联系人审核功能。");
    const result = await response.json();
    if (!response.ok || result.success === false) throw new Error(result.error || "无法加载联系人，请重试。");
    return result;
}

export function RecipientReviewPanel({ campaignId, campaignName, previewMode = false, refreshKey = '', onExclusionsChange }: {
    campaignId: string;
    campaignName?: string;
    previewMode?: boolean;
    refreshKey?: string;
    onExclusionsChange: () => void;
}) {
    const [scope, setScope] = useState<'campaign' | 'all'>('campaign');
    const [filter, setFilter] = useState<RecipientFilter>('hard_bounced');
    const [search, setSearch] = useState('');
    const [pageNumber, setPageNumber] = useState(0);
    const [page, setPage] = useState<RecipientPage | null>(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [reason, setReason] = useState('');
    const [selected, setSelected] = useState<Map<string, ReviewedRecipient>>(new Map());
    const [revision, setRevision] = useState(0);
    const [previewRows, setPreviewRows] = useState(() => previewRecipients(campaignId));
    const [syncStatus, setSyncStatus] = useState<{ running: boolean; automatic: boolean; mailboxes: Array<{ mailbox: string; lastCheckedAt: string | null; lastError: string | null; hasMore: boolean }> } | null>(null);
    const mounted = useRef(true);
    const selectionScope = `${scope}:${scope === 'campaign' ? campaignId : ''}:${filter}:${search}`;
    const selectionScopeRef = useRef(selectionScope);
    selectionScopeRef.current = selectionScope;
    const ready = scope === 'all' || !!campaignId;
    const query = useMemo(() => {
        const params = new URLSearchParams({ filter, search: search.trim(), limit: String(PAGE_SIZE), offset: String(pageNumber * PAGE_SIZE) });
        if (scope === 'campaign' && campaignId) params.set('campaignId', campaignId);
        return params;
    }, [filter, search, pageNumber, scope, campaignId]);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    useEffect(() => {
        if (previewMode) return;
        let active = true;
        const refresh = async () => {
            try {
                const response = await authFetch('/bulk-send/bounces/sync-status');
                if (response.ok) { const data = await response.json(); if (active) setSyncStatus(data); }
            } catch { /* Contact loading reports connectivity errors separately. */ }
        };
        void refresh();
        const timer = setInterval(() => { void refresh(); setRevision((value) => value + 1); }, 30_000);
        return () => { active = false; clearInterval(timer); };
    }, [previewMode]);

    async function checkBounces() {
        setBusy('sync'); setError('');
        try {
            const response = await authFetch('/bulk-send/bounces/sync', { method: 'POST' });
            const result = await response.json();
            if (!response.ok) throw new Error(result.error || '无法开始检查退信，请重试。');
            if (mounted.current) setNotice('已开始检查退信，列表会自动刷新。历史邮件较多时会分批处理。');
        } catch (err) { if (mounted.current) setError(err instanceof Error ? err.message : String(err)); }
        finally { if (mounted.current) setBusy(''); }
    }

    useEffect(() => {
        setSelected(new Map());
        setPageNumber(0);
        setNotice('');
    }, [selectionScope]);

    useEffect(() => {
        const controller = new AbortController();
        setPage(null);
        setError('');
        if (!ready) { setLoading(false); return () => controller.abort(); }
        setLoading(true);
        // Debounce typing; abort earlier requests when the selected list changes.
        const timer = setTimeout(async () => {
            try {
                let result: RecipientPage;
                if (previewMode) {
                    const matching = previewRows.filter((row) => JSON.stringify(row).toLowerCase().includes(search.trim().toLowerCase()));
                    const filtered = matching.filter((row) => filter === 'all' || (filter === 'hard_bounced' ? row.hardBounced : filter === 'failed' ? row.hasFailure : !!row.suppression));
                    result = {
                        recipients: filtered.slice(pageNumber * PAGE_SIZE, (pageNumber + 1) * PAGE_SIZE), total: filtered.length,
                        summary: { total: matching.length, hardBounced: matching.filter((row) => row.hardBounced).length, failed: matching.filter((row) => row.hasFailure).length, excluded: matching.filter((row) => row.suppression).length },
                        limit: PAGE_SIZE, offset: pageNumber * PAGE_SIZE, hasMore: (pageNumber + 1) * PAGE_SIZE < filtered.length,
                    };
                } else {
                    result = await loadPage(query, controller.signal);
                }
                if (controller.signal.aborted) return;
                if (!result.recipients.length && result.total > 0 && pageNumber > 0) {
                    setPageNumber(Math.max(Math.ceil(result.total / PAGE_SIZE) - 1, 0));
                    return;
                }
                setPage(result);
                setSelected((current) => {
                    const next = new Map(current);
                    result.recipients.forEach((recipient) => { if (next.has(recipient.email)) next.set(recipient.email, recipient); });
                    return next;
                });
            } catch (err) {
                if (!controller.signal.aborted) setError(err instanceof Error ? err.message : String(err));
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        }, search ? 250 : 0);
        return () => { clearTimeout(timer); controller.abort(); };
    }, [query, ready, previewMode, previewRows, filter, search, pageNumber, revision, refreshKey]);

    function toggle(recipient: ReviewedRecipient) {
        setSelected((current) => {
            const next = new Map(current);
            if (next.has(recipient.email)) next.delete(recipient.email);
            else next.set(recipient.email, recipient);
            return next;
        });
    }

    const pageRows = page?.recipients || [];
    const allPageSelected = pageRows.length > 0 && pageRows.every((recipient) => selected.has(recipient.email));
    const selectedRecipients = Array.from(selected.values());
    const toExclude = selectedRecipients.filter((recipient) => !recipient.suppression);

    function togglePage() {
        setSelected((current) => {
            const next = new Map(current);
            pageRows.forEach((recipient) => {
                if (allPageSelected) next.delete(recipient.email);
                else next.set(recipient.email, recipient);
            });
            return next;
        });
    }

    async function excludeSelected() {
        if (!toExclude.length || busy) return;
        const currentScope = selectionScope;
        const emails = toExclude.map((recipient) => recipient.email);
        setBusy('exclude'); setError(''); setNotice('');
        try {
            let excludedCount = emails.length;
            if (previewMode) {
                setPreviewRows((current) => current.map((recipient) => emails.includes(recipient.email) ? {
                    ...recipient, suppression: { reason: reason.trim() || "联系人审核后手动排除", source: 'manual_recipient_review', createdAt: new Date().toISOString() },
                } : recipient));
            } else {
                const response = await authFetch('/bulk-send/recipients/exclude', {
                    method: 'POST', body: JSON.stringify({ emails, reason: reason.trim() || undefined }),
                });
                const result = await response.json();
                if (!response.ok || result.success === false) throw new Error(result.error || "无法保存排除名单，请重试。");
                excludedCount = result.excludedCount;
            }
            if (!mounted.current) return;
            // Exclusions apply by email even if the user has since opened another list.
            onExclusionsChange();
            if (selectionScopeRef.current === currentScope) {
                setSelected(new Map());
                setReason('');
                setNotice(`已${previewMode ? '在本次预览中' : ''}排除 ${excludedCount} 个邮箱，后续发送将跳过这些邮箱。联系人资料和失败记录仍会保留。`);
            }
            setRevision((value) => value + 1);
        } catch (err) {
            if (mounted.current) setError(err instanceof Error ? err.message : String(err));
        } finally {
            if (mounted.current) setBusy('');
        }
    }

    async function exportContacts(onlySelected = false) {
        if (busy || !page) return;
        setBusy('export'); setError('');
        try {
            let recipients = onlySelected ? selectedRecipients : pageRows;
            if (!onlySelected && !previewMode) {
                const collected = new Map<string, ReviewedRecipient>();
                const params = new URLSearchParams(query);
                params.set('limit', '500');
                let offset = 0;
                let expectedTotal: number | undefined;
                while (true) {
                    if (!mounted.current) return;
                    params.set('offset', String(offset));
                    const result = await loadPage(params);
                    if (expectedTotal !== undefined && result.total !== expectedTotal) throw new Error("导出期间联系人列表发生变化，请刷新后重新导出。");
                    expectedTotal = result.total;
                    result.recipients.forEach((recipient) => collected.set(recipient.email, recipient));
                    if (!result.hasMore) break;
                    if (!result.recipients.length) throw new Error("部分联系人无法加载，导出已停止，请重试。");
                    offset += result.limit;
                }
                if (collected.size !== expectedTotal) throw new Error("导出期间联系人列表发生变化，请刷新后重新导出。");
                recipients = Array.from(collected.values());
            }
            const rows = recipientExportRows(recipients);
            if (!rows.length) return;
            const workbook = XLSX.utils.book_new();
            const sheet = XLSX.utils.json_to_sheet(rows);
            sheet['!cols'] = Object.keys(rows[0]).map((key) => ({ wch: key === "邮箱" ? 35 : 24 }));
            XLSX.utils.book_append_sheet(workbook, sheet, "联系人与失败记录");
            XLSX.writeFile(workbook, `${previewMode ? '示例_' : ''}${onlySelected ? '所选联系人' : FILTERS.find((item) => item.key === filter)?.label}_${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch (err) {
            if (mounted.current) setError(err instanceof Error ? err.message : String(err));
        } finally {
            if (mounted.current) setBusy('');
        }
    }

    return <section className="card bulk-section recipient-review" id="recipient-review" aria-label="硬退信与联系人排除">
        <div className="bulk-section-head">
            <div><div><h3>硬退信与联系人排除</h3><p>单独汇总硬退信邮箱，保留姓名、公司、所属列表和变量，后续发送自动跳过。</p></div></div>
            <button className="btn btn-outline btn-sm" disabled={loading || !!busy || !ready} onClick={() => setRevision((value) => value + 1)} aria-label="刷新联系人"><RefreshCw size={13} /></button>
        </div>
        <div className="recipient-bounce-note"><ShieldCheck size={17} /><div><strong>硬退信自动排除</strong><p>识别邮箱不存在、地址无效等永久问题。邮箱已满、连接超时和发送限流不会自动归入硬退信。</p>
            <small>{previewMode ? '当前展示示例数据。' : syncStatus ? `${syncStatus.automatic ? '自动检查已开启' : '自动检查未开启'} · 已检查 ${syncStatus.mailboxes.length} 个发件邮箱${syncStatus.mailboxes.some((box) => box.hasMore) ? ' · 历史退信正在分批检查' : ''}` : '发送当场被永久拒收，以及收信检查发现的硬退信，都会记录在此处。'}</small>
            {!!syncStatus?.mailboxes.some((box) => box.lastError) && <details><summary>部分邮箱检查失败，点击查看</summary>{syncStatus.mailboxes.filter((box) => box.lastError).map((box) => <p key={box.mailbox}>{box.mailbox}：{box.lastError}</p>)}</details>}
        </div>{!previewMode && <button className="btn btn-outline btn-sm" disabled={!!busy || syncStatus?.running} onClick={() => void checkBounces()}><RefreshCw size={13} />{busy === 'sync' || syncStatus?.running ? '正在检查退信…' : '检查新退信'}</button>}</div>
        <div className="recipient-toolbar">
            <label className="bulk-field"><span>列表范围</span><select className="input" value={scope} disabled={!!busy} onChange={(event) => setScope(event.target.value as 'campaign' | 'all')}><option value="campaign">当前活动</option><option value="all">全部可访问活动</option></select></label>
            <label className="bulk-field recipient-search"><span>查找联系人</span><span className="recipient-search-input"><Search size={15} /><input className="input" type="search" value={search} disabled={!!busy} onChange={(event) => setSearch(event.target.value)} placeholder="搜索邮箱、姓名、公司或自定义变量" /></span></label>
            <button className="btn btn-outline btn-sm" disabled={!page?.total || loading || !!busy} onClick={() => void exportContacts()}><Download size={13} /> {busy === 'export' ? "正在导出…" : "导出筛选结果"}</button>
        </div>
        <div className="recipient-scope-label">{previewMode ? "示例联系人 · 操作仅在本次预览生效" : scope === 'all' ? "你有权限访问的全部活动" : campaignName || "请先在上方选择已有活动"}<span>按邮箱去重展示 · 保留所有关联列表资料</span></div>
        <div className="recipient-filters" role="tablist" aria-label="联系人状态">
            {FILTERS.map((item) => <button key={item.key} role="tab" aria-selected={filter === item.key} className={filter === item.key ? 'active' : ''} disabled={!!busy} onClick={() => setFilter(item.key)}>{item.label}<span>{page ? page.summary[item.count].toLocaleString() : '—'}</span></button>)}
        </div>
        {error && <div className="bulk-alert danger" role="alert"><AlertCircle size={16} /><span>{error}</span></div>}
        {notice && <div className="bulk-alert success" role="status"><CheckCircle2 size={16} /><span>{notice}</span></div>}
        {selected.size > 0 && <div className="recipient-selection">
            <div><strong>已选择 {selected.size} 个邮箱</strong><button className="recipient-text-button" disabled={!!busy} onClick={() => setSelected(new Map())}>清除选择</button><button className="recipient-text-button" disabled={!!busy} onClick={() => void exportContacts(true)}>导出所选联系人</button></div>
            <div className="recipient-exclude-actions"><input className="input" aria-label="排除原因" value={reason} maxLength={500} disabled={!!busy} onChange={(event) => setReason(event.target.value)} placeholder="填写排除原因（可选）" /><button className="btn recipient-exclude-button" disabled={!toExclude.length || toExclude.length > 5000 || !!busy || loading} onClick={() => void excludeSelected()}><Ban size={14} />{busy === 'exclude' ? '正在保存…' : `排除 ${toExclude.length} 个邮箱，后续不再发送`}</button></div>
            <small>排除后，该邮箱在所有活动及以后导入的列表中都会被跳过。联系人资料仍会保留。已经开始发送的邮件无法撤回。</small>
            {toExclude.length > 5000 && <small>每次最多可选择 5,000 个邮箱。</small>}
        </div>}
        <div className="recipient-table-wrap" aria-busy={loading}>
            <table className="data-table recipient-table"><thead><tr>
                <th><input type="checkbox" aria-label="选择本页全部联系人" checked={allPageSelected} disabled={loading || !!busy || !pageRows.length} onChange={togglePage} /></th>
                <th>联系人／邮箱</th><th>公司与所属列表</th><th>{filter === 'hard_bounced' ? '硬退信原因与时间' : '退信／失败记录'}</th><th>后续发送</th>
            </tr></thead><tbody>
                {loading ? <tr><td colSpan={5} className="bulk-empty-cell">正在加载联系人…</td></tr>
                    : !pageRows.length ? <tr><td colSpan={5} className="bulk-empty-cell">{error ? '暂时无法获取联系人资料。' : !ready ? '请在上方选择活动，或切换为全部活动。' : filter === 'hard_bounced' ? '当前范围内暂无已确认的硬退信邮箱。' : filter === 'failed' ? '当前范围内没有发送失败或等待重试的邮箱。' : filter === 'excluded' ? '当前范围内没有已排除的邮箱。' : '没有符合筛选条件的联系人。'}</td></tr>
                    : pageRows.map((recipient) => {
                        const contact = recipient.contacts.find((member) => member.campaignId === campaignId) || recipient.contacts[0];
                        return <tr key={recipient.email} className={selected.has(recipient.email) ? 'recipient-selected' : ''}>
                            <td><input type="checkbox" aria-label={`选择 ${recipient.email}`} checked={selected.has(recipient.email)} disabled={!!busy} onChange={() => toggle(recipient)} /></td>
                            <td><strong>{contact?.name || "未填写姓名"}</strong><span className="recipient-email">{recipient.email}</span><small>{contact?.title || '—'}</small>
                                <details className="recipient-details"><summary>查看联系人资料与变量</summary>{recipient.contacts.map((member) => <div key={member.id}>
                                    <strong>{member.campaignName}</strong><dl>
                                        {Object.entries({ 姓名: member.name, 职位: member.title, 公司: member.companyName, 国家: member.country, 国家代码: member.countryCode, 语言: member.language, 状态: zh(member.status), 来源链接: member.sourceUrl }).map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{value || '—'}</dd></div>)}
                                        {Object.entries(member.customFields || {}).map(([key, value]) => <div key={`custom-${key}`}><dt>{key}<code>{`{{custom.${key}}}`}</code></dt><dd>{fieldText(value) || '—'}</dd></div>)}
                                    </dl>
                                </div>)}</details>
                            </td>
                            <td><strong>{contact?.companyName || '—'}</strong><small>{contact?.campaignName || '—'}</small>{recipient.contacts.length > 1 && <small className="recipient-linked">关联 {recipient.contacts.length} 条联系人记录</small>}</td>
                            <td>{recipient.hardBounced ? <><span className="job-status danger">硬退信</span><small>{recipient.lastHardBounce ? `${zh(recipient.lastHardBounce.source)} · ${recipient.lastHardBounce.statusCode || ''}` : '历史硬退信记录'}</small><p>{recipient.lastHardBounce?.diagnostic || recipient.suppression?.reason || '未记录详细退信原因'}</p>{recipient.lastHardBounce && <small>{new Date(recipient.lastHardBounce.occurredAt).toLocaleString('zh-CN')} · 共 {recipient.hardBounceCount} 次</small>}</> : recipient.lastFailure ? <><span className={`job-status ${recipient.lastFailure.status === 'queued' ? 'warning' : 'danger'}`}>{recipient.lastFailure.status === 'queued' ? '等待重试' : zh(recipient.lastFailure.status)}</span><small>{zh(recipient.lastFailure.errorCategory)}{recipient.lastFailure.errorCode ? ` · ${recipient.lastFailure.errorCode}` : ''}</small><p>{recipient.lastFailure.errorMessage || '未记录具体失败原因'}</p><small>{new Date(recipient.lastFailure.occurredAt).toLocaleString('zh-CN')} · 涉及 {recipient.failureCount} 个批次</small></> : <span className="recipient-muted">暂无失败记录</span>}</td>
                            <td>{recipient.suppression ? <><span className="recipient-excluded"><ShieldCheck size={12} /> 已排除</span><small>{zh(recipient.suppression.source)}</small><p>{zh(recipient.suppression.reason)}</p></> : <><span className="recipient-muted">未排除</span><small>发送前仍会检查资格</small></>}</td>
                        </tr>;
                    })}
            </tbody></table>
        </div>
        <div className="recipient-footer"><span>{page ? `第 ${page.total ? page.offset + 1 : 0}–${Math.min(page.offset + PAGE_SIZE, page.total)} 条，共 ${page.total.toLocaleString()} 个邮箱` : '—'}<small>硬退信来自实际发送结果或已核对的退信通知，不包含模拟测试。原始错误信息保留原文。</small></span><div><button className="btn btn-outline btn-sm" aria-label="上一页联系人" disabled={!pageNumber || loading || !!busy} onClick={() => setPageNumber((value) => value - 1)}><ChevronLeft size={15} /></button><button className="btn btn-outline btn-sm" aria-label="下一页联系人" disabled={!page?.hasMore || loading || !!busy} onClick={() => setPageNumber((value) => value + 1)}><ChevronRight size={15} /></button></div></div>
    </section>;
}
